import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { PersonAvatarVariant } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Pencil, Reset } from '@factorialco/f0-react/icons/app'
import {
  TrainingClassFragment,
  TrainingDetailFragment,
  TrainingDetailParticipantsFragment,
  TrainingSessionInstructorFragment,
  TrainingsDevLmsFeatureFlagDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

type Props = {
  training: TrainingDetailFragment
  trainingClasses: TrainingClassFragment[]
  participants: TrainingDetailParticipantsFragment[] | []
  instructors: TrainingSessionInstructorFragment[] | []
}

const TrainingDetailHeader: FC<Props> = ({
  trainingClasses,
  training,
  participants,
  instructors,
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const goToEditPage = () => {
    navigate(
      navigation.trainings.list.details_revamp.editTraining.view.fullPath({
        id: training.id,
      }),
      { state: { from: location.pathname } }
    )
  }

  const onPublish = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.startTraining.fullPath({
        id: training.id,
      })
    )
  }

  const onRevert = () => {
    navigate(
      navigation.trainings.list.details_revamp.view.stopTraining.fullPath({
        id: training.id,
      })
    )
  }

  const avatarsList: PersonAvatarVariant[] = participants.length
    ? participants.map(({ access }) => ({
        firstName: access?.firstName ?? '',
        lastName: access?.lastName ?? '',
        type: 'person',
        src: buildUrl(access?.avatar ?? ''),
      }))
    : []

  const groupList = trainingClasses.map((group) => group.name)

  const instructorsList: PersonAvatarVariant[] = (() => {
    const instructorsSet = new Set()
    const instructorsUniqueIds: TrainingSessionInstructorFragment[] = instructors?.length
      ? instructors.filter(
          (item) => !instructorsSet.has(item.employee.id) && instructorsSet.add(item.employee.id)
        )
      : []
    return instructorsUniqueIds.length
      ? instructorsUniqueIds.map((instructor) => ({
          firstName: instructor?.employee?.firstName ?? '',
          lastName: instructor?.employee?.lastName ?? '',
          type: 'person',
          src: buildUrl(instructor?.employee?.avatar?.url ?? ''),
        }))
      : []
  })()
  const { company } = useCurrentSession()
  const { data: featureFlagData } = useQuery(TrainingsDevLmsFeatureFlagDocument, {
    variables: { companyId: company?.id },
  })
  const hasDuration = Boolean(
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (n) => n.name === 'dev_training_lms' && n.enabled == true
    )
  )
  const getTotalDuration = () => {
    if (!training.totalDuration) return '-'
    const totalMinutes = training.totalDuration * 60
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes % 60)
    return `${hours}h ${minutes}m`
  }
  return (
    <ResourceHeader
      title={training.name}
      metadata={[
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.type'),
          value: {
            type: 'text',
            content: training.external
              ? i18n.t('trainings.trainings_revamp.training_detail.overview.external')
              : i18n.t('trainings.trainings_revamp.training_detail.overview.internal'),
          },
        },
        ...(hasDuration
          ? [
              {
                label: i18n.t(
                  'trainings.trainings_revamp.training_detail.header.metadata.total_duration'
                ),
                value: {
                  type: 'text' as const,
                  content: getTotalDuration(),
                },
              },
            ]
          : []),
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.groups'),
          value: {
            type: 'data-list',
            data: groupList,
          },
        },
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.participants'),
          value: {
            type: 'list',
            variant: 'person',
            avatars: avatarsList,
          },
        },
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header.metadata.instructors'),
          value: {
            type: 'list' as const,
            variant: 'person' as const,
            avatars: instructorsList,
          },
        },
      ]}
      status={{
        label: '',
        text:
          training.status === 'active'
            ? i18n.t('trainings.trainings_revamp.training_detail.header.status.published')
            : i18n.t('trainings.trainings_revamp.training_detail.header.status.draft'),
        variant: training.status === 'active' ? 'positive' : 'neutral',
      }}
      primaryAction={
        training.status !== 'active'
          ? {
              label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.publish'),
              onClick: onPublish,
            }
          : undefined
      }
      secondaryActions={[
        training.status === 'active' && {
          label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.revert'),
          onClick: onRevert,
          variant: 'critical' as const,
          icon: Reset,
        },
        {
          label: i18n.t('trainings.trainings_revamp.training_detail.header_buttons.edit'),
          icon: Pencil,
          onClick: goToEditPage,
        },
      ].filter(Boolean)}
    />
  )
}

export default TrainingDetailHeader
